import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftRight, Plus, RefreshCw, Waypoints, X } from 'lucide-react';
import type { Ipv4WithMask, Resource, VpnEndpoint, VpnLink, VpnLinkView } from '@itr/shared';
import { formatRelative, interfaceAddressesOf, isMikrotik, parseIpv4WithMask, prefixToNetmask, vpnDiscoveryOf } from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Badge, Health, useFetch, useToast } from './ui';

/** One address shown on a map node or in the addresses table. */
export interface NodeAddress {
  ip: string;
  prefix: number | null;
  /** Interface or tunnel name it came from. */
  label: string;
  tunnel: boolean;
}

/**
 * Every address worth showing on each resource: its interfaces as SSH/SNMP
 * read them, plus its end of every VPN link (a manually recorded tunnel
 * address may be the only place its mask is known).
 */
export function nodeAddressMap(resources: Resource[], links: VpnLinkView[]): Map<string, NodeAddress[]> {
  const map = new Map<string, NodeAddress[]>();
  const put = (id: string, addr: NodeAddress) => {
    const list = map.get(id) ?? [];
    const existing = list.find((a) => a.ip === addr.ip);
    if (!existing) list.push(addr);
    else if (existing.prefix === null && addr.prefix !== null) existing.prefix = addr.prefix;
    map.set(id, list);
  };
  for (const r of resources) {
    for (const a of interfaceAddressesOf(r)) put(r.id, { ip: a.ip, prefix: a.prefix, label: a.interface, tunnel: a.tunnel });
  }
  for (const link of links) {
    for (const end of [link.a, link.b]) {
      if (end.resourceId && end.address) put(end.resourceId, { ip: end.address.ip, prefix: end.address.prefix, label: link.label, tunnel: true });
    }
  }
  return map;
}

/** `10.0.0.1  255.255.255.0` — the dotted mask spelled out, the prefix in the tooltip. */
export function IpMask({ address, className }: { address: Ipv4WithMask | null; className?: string }) {
  if (!address) return <span className={className}>—</span>;
  return (
    <span className={className} title={address.prefix === null ? 'Netmask unknown' : `${address.ip}/${address.prefix}`}>
      {address.ip}
      <span className="vpn-mask">{address.prefix === null ? ' / ?' : ` / ${prefixToNetmask(address.prefix)}`}</span>
    </span>
  );
}

const SOURCE_LABEL: Record<VpnLinkView['source'], string> = {
  manual: 'Manual',
  ppp: 'PPP session',
  p2p: 'Point-to-point',
};

function EndpointName({ end }: { end: VpnEndpoint & { health?: VpnLinkView['a']['health'] } }) {
  if (!end.resourceId) return <span className="subtle">{end.name}</span>;
  return (
    <Link to={`/resources/${end.resourceId}`} className="row" style={{ gap: 6, color: 'inherit' }}>
      {end.health && <Health health={end.health} label={false} />}
      <span className="truncate">{end.name}</span>
    </Link>
  );
}

/** Every link as a table: both ends with address and netmask, and how it was found. */
export function VpnLinksTable({ links }: { links: VpnLinkView[] }) {
  if (links.length === 0) return null;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>From</th>
            <th>Address / netmask</th>
            <th aria-label="to" />
            <th>To</th>
            <th>Address / netmask</th>
            <th>Connection</th>
            <th>Tunnel</th>
          </tr>
        </thead>
        <tbody>
          {links.map((link) => (
            <tr key={link.id}>
              <td>
                <EndpointName end={link.a} />
              </td>
              <td>
                <IpMask address={link.a.address} className="mono" />
              </td>
              <td className="subtle">
                <ArrowLeftRight size={13} />
              </td>
              <td>
                <EndpointName end={link.b} />
              </td>
              <td>
                <IpMask address={link.b.address} className="mono" />
              </td>
              <td>
                <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                  <Badge>{SOURCE_LABEL[link.source]}</Badge>
                  {link.label && <span>{link.label}</span>}
                </div>
                {(link.uptime || link.callerId || link.routes?.length) && (
                  <div className="subtle" style={{ fontSize: 11.5, marginTop: 3 }}>
                    {[
                      link.uptime && `up ${link.uptime}`,
                      link.callerId && `from ${link.callerId}`,
                      link.routes?.length ? `routes ${link.routes.join(', ')}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                )}
              </td>
              <td>
                {link.status ? (
                  <div>
                    <Health
                      health={link.status.state}
                      reason={`${link.status.detail}${link.status.downSince ? `\nDown since ${new Date(link.status.downSince).toLocaleString()}` : ''}`}
                    />
                    <div className="subtle" style={{ fontSize: 11.5, marginTop: 3 }}>
                      checked {formatRelative(link.status.checkedAt)}
                      {link.status.checkedFrom && ` from ${link.status.checkedFrom}`}
                    </div>
                  </div>
                ) : (
                  <span className="subtle">not checked yet</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** POSTs an SSH VPN read of one MikroTik; the caller refetches afterwards. */
export async function runVpnDiscovery(resourceId: string): Promise<{ addresses: number; sessions: number }> {
  return api.post<{ addresses: number; sessions: number }>(`/api/tools/resource/${resourceId}/vpn-discover`);
}

/**
 * Resource page card: this device's addresses with netmasks, its VPN links,
 * and — on a MikroTik — a button to (re)read them over SSH.
 */
export function ResourceVpnCard({ resource, onDiscovered }: { resource: Resource; onDiscovered: () => void }) {
  const { can } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const links = useFetch(() => api.get<VpnLinkView[]>('/api/vpn-links', { resourceId: resource.id }), [resource.id, resource.updatedAt]);
  const addresses = interfaceAddressesOf(resource);
  const discovery = vpnDiscoveryOf(resource);
  const canDiscover = can('tools:run') && isMikrotik(resource);

  if (addresses.length === 0 && (links.data ?? []).length === 0 && !canDiscover) return null;

  const discover = async () => {
    setBusy(true);
    try {
      const result = await runVpnDiscovery(resource.id);
      toast(`Read ${result.addresses} address${result.addresses === 1 ? '' : 'es'} and ${result.sessions} PPP session${result.sessions === 1 ? '' : 's'}`, 'good');
      onDiscovered();
      links.reload();
    } catch (err) {
      toast('VPN discovery failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <Waypoints size={14} />
        <span className="card-title">VPN & addresses</span>
        <span className="grow" />
        {discovery && <span className="subtle" style={{ fontSize: 12 }}>read {formatRelative(discovery.ts)}</span>}
        {canDiscover && (
          <button className="btn btn-sm" onClick={discover} disabled={busy} title="Read addresses, PPP active sessions and tunnel routes over SSH">
            {busy ? <span className="spinner" style={{ width: 12, height: 12 }} /> : <RefreshCw size={12} />}
            {busy ? 'Reading…' : 'Discover over SSH'}
          </button>
        )}
      </div>
      {addresses.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Interface</th>
                <th>Address</th>
                <th>Netmask</th>
                <th>Peer</th>
              </tr>
            </thead>
            <tbody>
              {addresses.map((a) => (
                <tr key={`${a.interface}-${a.ip}`}>
                  <td>
                    <span className="mono">{a.interface}</span> {a.tunnel && <Badge intent="info">tunnel</Badge>}
                  </td>
                  <td className="mono">
                    {a.ip}/{a.prefix}
                  </td>
                  <td className="mono">{prefixToNetmask(a.prefix)}</td>
                  <td className="mono subtle">{a.network ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <VpnLinksTable links={links.data ?? []} />
      {addresses.length === 0 && (links.data ?? []).length === 0 && (
        <div className="subtle" style={{ padding: '12px 16px', fontSize: 12.5 }}>
          No addresses or VPN links known yet. Discovery reads <span className="mono">/ip address</span>,{' '}
          <span className="mono">/ppp active</span> and the routes into each tunnel.
        </div>
      )}
    </div>
  );
}

/**
 * The edit form's list of hand-recorded tunnels: the far-end resource (or
 * just a name, for a peer that isn't monitored here) and both tunnel
 * addresses, each taking `/24` or `/255.255.255.0`.
 */
export function VpnLinkEditor({ selfId, rows, onChange }: { selfId: string; rows: VpnLink[]; onChange: (rows: VpnLink[]) => void }) {
  const peers = useFetch(() => api.get<Resource[]>('/api/resources', { limit: 500 }), []);
  const update = (i: number, patch: Partial<VpnLink>) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const invalid = (v: string) => Boolean(v.trim()) && parseIpv4WithMask(v) === null;
  const options = (peers.data ?? []).filter((r) => r.id !== selfId).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="col" style={{ gap: 10, marginBottom: 8 }}>
      {rows.map((row, i) => (
        <div key={i} className="col" style={{ gap: 6, paddingBottom: 10, borderBottom: 'var(--itr-border-w) solid var(--itr-border)' }}>
          <div className="row" style={{ gap: 6 }}>
            <select className="input" value={row.peerId ?? ''} onChange={(e) => update(i, { peerId: e.target.value || null })}>
              <option value="">Peer not monitored here</option>
              {options.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                  {r.address ? ` — ${r.address}` : ''}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-icon btn-sm" onClick={() => onChange(rows.filter((_, idx) => idx !== i))} aria-label="Remove">
              <X size={13} />
            </button>
          </div>
          <input className="input" placeholder="Label, e.g. SSTP to HQ" value={row.label} onChange={(e) => update(i, { label: e.target.value })} />
          <div className="row" style={{ gap: 6 }}>
            <input
              className="input mono"
              placeholder="This end, 10.8.0.2/24"
              value={row.localAddress}
              onChange={(e) => update(i, { localAddress: e.target.value })}
              style={{ borderColor: invalid(row.localAddress) ? 'var(--itr-critical)' : undefined }}
            />
            <input
              className="input mono"
              placeholder="Far end, 10.8.0.1/24"
              value={row.remoteAddress}
              onChange={(e) => update(i, { remoteAddress: e.target.value })}
              style={{ borderColor: invalid(row.remoteAddress) ? 'var(--itr-critical)' : undefined }}
            />
          </div>
        </div>
      ))}
      <button type="button" className="btn btn-sm" onClick={() => onChange([...rows, { peerId: null, label: '', localAddress: '', remoteAddress: '' }])}>
        <Plus size={12} /> Add VPN link
      </button>
    </div>
  );
}
